import React, { useMemo } from 'react';
import { ArrowLongRightIcon } from '@heroicons/react/24/outline';
import { SOURCES, normalizeMatrix, perTimestampScores } from './dayDetailMath';
import { SOURCE_COLOR, NOT_APPLICABLE_HATCH } from './sourceColors';
import { getHourMinuteInResourceTz, getResourceTimezoneLabel } from '../../../../../utils/datetime';

// Short labels for the strip legend; colours come from the shared source map.
const SOURCE_SHORT = {
  [SOURCES.LIVE]: 'live',
  [SOURCES.FWD_FILL]: 'forward-fill',
  [SOURCES.CROSS_HORIZON]: 'cross-horizon',
};
const SOURCE_ORDER = [SOURCES.LIVE, SOURCES.FWD_FILL, SOURCES.CROSS_HORIZON];

const BAR_MAX = 80; // px — tallest bar height
const LABEL_HEADROOM = 48; // px — fixed space above bars for the rotated labels
const CHART_H = BAR_MAX + LABEL_HEADROOM; // fixed → the card never changes height
const MEAN_GUTTER = 46; // px — right-hand space reserved for the mean reference label
const ZERO_EPS = 5e-4; // below this a value rounds to "0.000" — treat as zero for bar heights
const LEFT_PAD = 22; // px — left inset so the chart isn't flush to the edge

// "HH:mm CEST" in the resource timezone, labelled at the instant itself.
const fmtTime = (iso) => {
  const hm = getHourMinuteInResourceTz(iso);
  return hm ? `${hm} ${getResourceTimezoneLabel(undefined, new Date(iso))}` : iso || '';
};
const fmt = (v, d = 2) => (v == null || !Number.isFinite(v) ? '—' : Number(v).toFixed(d));

// Squared residuals are in MW² and routinely run to the millions, so the
// human-readable build-up shows a compact form: 2_875_519 → "2.9M", 1485 → "1.5K".
const compact = (v) => {
  if (v == null || !Number.isFinite(v)) return '—';
  if (Math.abs(v) < ZERO_EPS) return '0'; // exact zero — no decimals
  if (Math.abs(v) >= 1000) {
    return new Intl.NumberFormat('en', { notation: 'compact', minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(v);
  }
  return Number(v).toFixed(3);
};

/**
 * The score build-up for one target timestamp, drawn as a bar chart: one bar
 * per gate-closure slot, height ∝ its squared contribution (o−f)², coloured by
 * fill source. A dashed line marks the arithmetic mean of those bars — the
 * per-timestamp MSE — and the reduction line shows how that rolls up to the
 * day's RMSE.
 *
 * Intraday (horizonOffset 0) shows the 24-slot chart. D+N (horizonOffset >= 1)
 * usually has a single covering submission, so the per-timestamp MSE is just
 * that one squared residual (no mean step) and we render a compact summary
 * instead of a one-bar chart.
 */
export default function SlotBuildupStrip({ matrixJson, timestampIndex, horizonOffset, pinned = false }) {
  const matrix = useMemo(() => normalizeMatrix(matrixJson), [matrixJson]);
  const perTs = useMemo(() => perTimestampScores(matrixJson), [matrixJson]);

  const idx = matrix && Number.isInteger(timestampIndex)
    ? Math.max(0, Math.min(timestampIndex, matrix.timestamps.length - 1))
    : 0;

  const slots = useMemo(() => {
    if (!matrix) return [];
    const residualRow = matrix.residuals[idx] || [];
    const sourceRow = matrix.sources[idx] || [];
    const leadRow = matrix.leadHours[idx] || [];
    return Array.from({ length: matrix.slotCount }, (_, s) => {
      const src = sourceRow[s] || SOURCES.MISSING;
      const notApplicable = src === SOURCES.NOT_APPLICABLE;
      const filled = src !== SOURCES.MISSING && !notApplicable;
      const r = typeof residualRow[s] === 'number' && Number.isFinite(residualRow[s]) ? residualRow[s] : null;
      return { s, src, filled, notApplicable, lead: leadRow[s], residual: r, sq: filled && r != null ? r * r : null };
    });
  }, [matrix, idx]);

  if (!matrix) return null;

  const isDN = typeof horizonOffset === 'number' && horizonOffset >= 1;
  const iso = matrix.timestamps[idx];
  const score = perTs[idx] || { mse: null, filledSlots: 0, missingSlots: matrix.slotCount };
  const filledCount = score.filledSlots;
  const rawMaxSq = slots.reduce((m, x) => (x.sq != null && x.sq > m ? x.sq : m), 0);
  // Anything below 5e-4 rounds to "0.000" at our display precision. Treat such a
  // day as flat (no bars): relative scaling would otherwise inflate sub-display
  // noise to differing heights even though every value reads as 0.000.
  const maxSq = rawMaxSq < ZERO_EPS ? 0 : rawMaxSq;
  const meanTop = maxSq > 0 && score.mse != null
    ? Math.max(2, CHART_H - Math.min(1, score.mse / maxSq) * BAR_MAX)
    : CHART_H - 1;

  // All rows share the bar content box so the component reads as one aligned
  // block: same left inset, same right edge (bars stop at the mean gutter).
  const rowStyle = { paddingLeft: LEFT_PAD, paddingRight: MEAN_GUTTER };

  const Header = (
    <div className="text-center">
      <div className="flex items-center justify-center gap-2">
        <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-brand-600 dark:text-brand-400">
          Score build-up
        </span>
        <span
          className={`rounded px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide ${
            pinned
              ? 'bg-brand-600 text-white'
              : 'bg-gray-200 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
          }`}
        >
          {pinned ? 'pinned' : 'preview'}
        </span>
      </div>
    </div>
  );

  // The two reduction steps, rendered as an equation rather than buttons.
  const Reduction = (
    <div className="flex flex-wrap items-center gap-2" style={rowStyle}>
      <Term
        label={filledCount <= 1 ? 'single slot · (o−f)²' : `per-t MSE @ ${fmtTime(iso)}`}
        value={`${compact(score.mse)} MW²`}
      />
      <div className="flex items-center gap-1.5 text-[10px] leading-tight text-gray-500 dark:text-gray-400">
        <ArrowLongRightIcon className="h-4 w-5 flex-shrink-0" />
        <span>
          averaged over the day, then √ →{' '}
          <span className="font-semibold text-brand-600 dark:text-brand-400">Target Day RMSE</span>{' '}
          (top)
        </span>
      </div>
    </div>
  );

  const allNotApplicable = slots.length > 0 && slots.every((x) => x.notApplicable);

  if (filledCount === 0) {
    return (
      <div data-testid="buildup-strip" className="rounded-lg border border-gray-200/70 dark:border-gray-600/70 bg-surface dark:bg-gray-870/40 p-4 space-y-3">
        {Header}
        {allNotApplicable ? (
          <div className="rounded-md border border-gray-300 dark:border-gray-600/60 bg-gray-50 dark:bg-gray-800/40 px-3 py-2 text-[11px] text-gray-600 dark:text-gray-300">
            No intraday session existed for any gate of {fmtTime(iso)} (its lookback
            predates the first intraday session). This timestamp is not applicable — it is
            excluded from the day and does not count against qualification.
          </div>
        ) : (
          <div className="rounded-md border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-[11px] text-amber-800 dark:text-amber-200">
            No slot is filled for {fmtTime(iso)} — this timestamp has no score, so the day
            fails qualification and is replaced by the peer-Q75 penalty.
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="buildup-strip" className="rounded-lg border border-brand-100 dark:border-brand-900/40 bg-brand-50/40 dark:bg-brand-950/20 p-4 space-y-3">
      {Header}

      {/* Legend sits above the bar plot. */}
      <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 text-[10px] text-gray-500 dark:text-gray-400">
        {SOURCE_ORDER.map((src) => (
          <span key={src} className="flex items-center gap-1">
            <span className="inline-block h-2.5 w-2.5 rounded-[2px]" style={{ background: SOURCE_COLOR[src] }} />
            {SOURCE_SHORT[src]}
          </span>
        ))}
        <span className="flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-[2px] border border-dashed border-gray-400" />
          missing
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-[2px] border border-gray-300 dark:border-gray-600" style={{ background: NOT_APPLICABLE_HATCH }} />
          n/a
        </span>
      </div>

      {isDN && matrix.slotCount === 1 ? (
        // Single covering submission: a bar chart of one bar is pointless.
        <div className="text-[11px] text-gray-600 dark:text-gray-300">
          One submission covers this timestamp, so the per-timestamp MSE is just its
          squared residual{slots[0]?.residual != null ? ` ((o−f)² = ${compact(slots[0].sq)} MW²)` : ''}.
        </div>
      ) : (
        <div>
          {/* Bar chart: height ∝ squared contribution, dashed line = mean (per-t MSE). */}
          <div className="relative" style={{ height: CHART_H }}>
            <div className="flex h-full items-end gap-px" style={{ paddingRight: MEAN_GUTTER, paddingLeft: LEFT_PAD }}>
              {slots.map((slot) => {
                if (!slot.filled) {
                  return (
                    <div
                      key={slot.s}
                      className="flex flex-1 min-w-[3px] flex-col items-center justify-end"
                      title={slot.notApplicable
                        ? `lead ${slot.lead == null ? '?' : `${slot.lead}h`} · not applicable — no session at this gate`
                        : undefined}
                    >
                      {slot.notApplicable ? (
                        <div className="w-full" style={{ height: 4, background: NOT_APPLICABLE_HATCH }} />
                      ) : (
                        <div className="w-full border-b border-dashed border-gray-300 dark:border-gray-600" style={{ height: 2 }} />
                      )}
                    </div>
                  );
                }
                const color = SOURCE_COLOR[slot.src] || SOURCE_COLOR[SOURCES.LIVE];
                const hPx = maxSq > 0 && slot.sq >= ZERO_EPS ? Math.max(2, (slot.sq / maxSq) * BAR_MAX) : 0;
                return (
                  <div
                    key={slot.s}
                    className="flex flex-1 min-w-[4px] flex-col items-center justify-end"
                    title={`lead ${slot.lead == null ? '?' : `${slot.lead}h`} · ${SOURCE_SHORT[slot.src] || slot.src} · residual ${fmt(slot.residual)} · (o−f)² = ${fmt(slot.sq)}`}
                  >
                    {/* Vertical value label, reads upward, above the bar. */}
                    <span
                      className="mb-0.5 leading-none font-mono tabular-nums font-semibold text-gray-700 dark:text-gray-200"
                      style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontSize: 10 }}
                    >
                      {compact(slot.sq)}
                    </span>
                    <div
                      data-testid="buildup-bar"
                      className="w-full rounded-t-[2px] transition-[height]"
                      style={{ height: hPx, background: color }}
                    />
                  </div>
                );
              })}
            </div>
            {/* Mean line across the bars; reference label sits in the right gutter. */}
            <div
              className="pointer-events-none absolute border-t-2 border-dashed border-red-500/55 dark:border-red-400/55"
              style={{ top: meanTop, right: MEAN_GUTTER, left: LEFT_PAD }}
            />
            <div
              className="pointer-events-none absolute right-0 pl-1 text-left leading-tight"
              style={{ top: meanTop, width: MEAN_GUTTER, transform: 'translateY(-50%)' }}
            >
              <span className="block text-[8px] font-semibold uppercase tracking-wide text-red-600 dark:text-red-400">mean</span>
              <span className="block text-[11px] font-semibold font-mono tabular-nums text-red-600 dark:text-red-400">{compact(score.mse)}</span>
              <span className="block text-[8px] text-red-500/80 dark:text-red-400/80">MW²</span>
            </div>
          </div>

          {/* Sparse lead axis: longest lead left → latest gate right. */}
          <div className="mt-1 flex justify-between text-[9px] text-gray-400 dark:text-gray-500" style={{ paddingRight: MEAN_GUTTER, paddingLeft: LEFT_PAD }}>
            <span>{matrix.slotCount - 1}h lead</span>
            <span>0h lead</span>
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400" style={rowStyle}>
            each bar = that slot's squared contribution (o−f)² · taller = larger error · every slot counts equally
          </div>
        </div>
      )}

      <div className="border-t border-brand-100 dark:border-brand-900/40 pt-3">
        {Reduction}
      </div>
    </div>
  );
}

function Term({ label, value, primary = false }) {
  return (
    <div
      className={`rounded-md px-3 py-1.5 ${
        primary
          ? 'bg-brand-600 text-white'
          : 'bg-surface dark:bg-gray-870 border border-gray-200/70 dark:border-gray-600/70 text-gray-800 dark:text-gray-100'
      }`}
    >
      <div className={`text-[9px] uppercase tracking-wide ${primary ? 'text-brand-100' : 'text-gray-400 dark:text-gray-500'}`}>
        {label}
      </div>
      <div className={`font-mono tabular-nums font-semibold ${primary ? 'text-base' : 'text-sm'}`}>{value}</div>
    </div>
  );
}
